import '../styles.css'

import { IconAlertOctagonFilled, IconAlertTriangleFilled, IconTrendingDown } from '@tabler/icons-react'
import { Tooltip } from 'antd'

import { FlagIconSwitch2 } from '../../../../_SiteWide/Flags/FlagIconSwitch'
import { StatusAvatar } from '../../../../_SiteWide/Flags/StatusAvatar'

const FLAG_ICON_SIZES = {
  tiny: '16px',
  small: '24px',
  medium: '27px',
}

export type FlagIconProps = {
  threshold?: number
  size: 'tiny' | 'small' | 'medium'
  severity?: 'error' | 'warning' | 'empty'
  tooltip?: string
}

function BaseRunoutIcon({ severity = 'error', size = 'medium', tooltip }: FlagIconProps) {
  const effectiveSeverity = severity === 'empty' ? 'error' : severity
  const backgroundStyle = severity === 'empty' ? 'background-red border-red' : 'border-black'

  const icon = ['error', 'empty'].includes(severity) ? (
    <IconAlertOctagonFilled style={{ color: `var(--theme-${effectiveSeverity})`, margin: '4px' }} />
  ) : (
    <IconAlertTriangleFilled style={{ color: `var(--theme-${effectiveSeverity})`, margin: '4px' }} />
  )

  return (
    <Tooltip title={tooltip}>
      <div className={`tank-alert-icon ${backgroundStyle} ${size}`}>
        {icon}
        <div
          className='background-block'
          style={{
            height: '5px',
            backgroundColor: `var(--theme-${effectiveSeverity})`,
          }}
        />
      </div>
    </Tooltip>
  )
}

export function EmptyAlertIcon({ size = 'medium' }: FlagIconProps) {
  return <BaseRunoutIcon size={size} severity='empty' tooltip='Tank is empty' />
}

export function RunoutWithinIcon({ threshold, severity = 'error', size = 'medium' }: FlagIconProps) {
  return (
    <BaseRunoutIcon size={size} severity={severity} tooltip={threshold ? `Runout within ${threshold} hours` : ''} />
  )
}

export function RunoutIcon({ severity = 'error', size = 'medium' }: FlagIconProps) {
  return <BaseRunoutIcon size={size} severity={severity} tooltip='Order ETA after tank runout time' />
}

export function PotentialRunoutIcon({ size = 'medium', threshold }: FlagIconProps) {
  return (
    <BaseRunoutIcon size={size} severity='warning' tooltip={`Order ETA within ${threshold} minutes of tank runout`} />
  )
}

export function NoFitIcon({ size = 'medium' }: FlagIconProps) {
  return (
    <Tooltip title='Order ETA prior to no fit time'>
      <div className={`tank-alert-icon border-black ${size}`}>
        <IconAlertOctagonFilled style={{ color: 'white', margin: '4px', zIndex: 1 }} />
        <div
          className='background-block'
          style={{
            height: FLAG_ICON_SIZES[size],
            backgroundColor: 'var(--theme-error',
          }}
        />
      </div>
    </Tooltip>
  )
}

export function PotentialNoFitIcon({ size = 'medium', threshold }: FlagIconProps) {
  return (
    <Tooltip title={`Order ETA within ${threshold} minutes of no fit time`}>
      <div className={`tank-alert-icon border-black ${size}`}>
        <IconAlertTriangleFilled style={{ color: 'white', margin: '4px', zIndex: 1 }} />
        <div
          className='background-block'
          style={{
            height: FLAG_ICON_SIZES[size],
            backgroundColor: 'var(--theme-warning',
          }}
        />
      </div>
    </Tooltip>
  )
}

export function DisconnectedIcon({ size = 'medium' }: FlagIconProps) {
  return (
    <StatusAvatar
      severity='error'
      type='stale'
      size={30}
      icon={<FlagIconSwitch2 flag={{ type: 'tank_reading', severity: 'error' }} />}
    />
  )
}

export function StaleIcon({ size = 'medium' }: FlagIconProps) {
  return (
    <StatusAvatar
      severity='warning'
      type='stale'
      size={30}
      icon={<FlagIconSwitch2 flag={{ type: 'tank_reading', severity: 'warning' }} />}
    />
  )
}

export function CreditHoldIcon({ size = 'medium' }: FlagIconProps) {
  return (
    <StatusAvatar
      severity='error'
      type='credit_hold'
      size={30}
      icon={<FlagIconSwitch2 flag={{ type: 'credit_hold', severity: 'error' }} />}
    />
  )
}

export function ComplianceHoldIcon({ size = 'medium' }: FlagIconProps) {
  return (
    <StatusAvatar
      severity='error'
      type='compliance_hold'
      size={30}
      icon={<FlagIconSwitch2 flag={{ type: 'compliance_hold', severity: 'error' }} />}
    />
  )
}

export function SalesVarianceIcon({ size = 'medium' }: FlagIconProps) {
  return <StatusAvatar severity='error' type='sales_variance' size={30} icon={<IconTrendingDown />} />
}
